import { Plus, MessageSquare, Trash2, ShieldCheck, LifeBuoy, CalendarClock, Sparkles, Wrench, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";

const QUICK_ACTIONS = [
  { key: "warranty", label: "File a warranty claim", icon: ShieldCheck, prompt: "I need to file a warranty claim for a faulty device." },
  { key: "support", label: "Get post-sales support", icon: LifeBuoy, prompt: "I'm having a technical problem after purchase and need support." },
  { key: "booking", label: "Book an appointment", icon: CalendarClock, prompt: "I'd like to book a service appointment." },
  { key: "sales", label: "Talk to sales", icon: Sparkles, prompt: "I'm interested in your managed IT services and would like a quote." },
];

export function Sidebar({ sessions, currentId, mode, onNew, onSelect, onDelete, onQuick, open, onClose }) {
  const close = () => onClose && onClose();
  const doNew = () => { onNew(); close(); };
  const doSelect = (id) => { onSelect(id); close(); };
  const doQuick = (p) => { onQuick(p); close(); };

  const inner = (
    <>
      <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
        <button
          data-testid="new-chat-button"
          onClick={doNew}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[var(--primary)] text-black font-semibold py-2.5 text-sm transition-colors duration-200 hover:bg-[var(--primary-hover)]"
        >
          <Plus size={16} /> New conversation
        </button>
        <button
          data-testid="sidebar-close"
          onClick={close}
          className="lg:hidden h-10 w-10 shrink-0 grid place-items-center rounded-xl border border-[var(--border)] text-[var(--text-muted)] hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-4 border-b border-[var(--border)]">
        <p className="text-xs font-mono-tech uppercase tracking-widest text-[var(--text-muted)] mb-3">Quick start</p>
        <div className="space-y-1.5">
          {QUICK_ACTIONS.map((q) => (
            <button
              key={q.key}
              data-testid={`quick-${q.key}`}
              onClick={() => doQuick(q.prompt)}
              className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)] transition-colors duration-200 hover:bg-white/5 hover:text-[var(--text-primary)] text-left"
            >
              <q.icon size={15} className="text-[var(--primary)] shrink-0" />
              <span className="truncate">{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-4 pb-2">
        <p className="text-xs font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">History</p>
      </div>
      <ScrollArea className="flex-1 px-2">
        <div className="space-y-1 pb-4">
          {sessions.length === 0 && (
            <p className="px-3 py-2 text-xs text-[var(--text-muted)]">No conversations yet.</p>
          )}
          {sessions.map((s) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className={`group flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer transition-colors duration-200 ${
                s.id === currentId ? "bg-white/8 text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-white/5"
              }`}
              onClick={() => doSelect(s.id)}
              data-testid={`session-${s.id}`}
            >
              <MessageSquare size={14} className="shrink-0 text-[var(--text-muted)]" />
              <span className="flex-1 truncate text-sm">{s.title}</span>
              <button
                data-testid={`delete-session-${s.id}`}
                onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                className="opacity-0 group-hover:opacity-100 text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--alert)]"
              >
                <Trash2 size={13} />
              </button>
            </motion.div>
          ))}
        </div>
      </ScrollArea>

      <div className="p-4 border-t border-[var(--border)]">
        <div className={`flex items-center gap-2 text-xs font-mono-tech ${mode === "technician" ? "text-[var(--tech-accent)]" : "text-[var(--primary)]"}`}>
          <Wrench size={13} />
          {mode === "technician" ? "TECH CO-PILOT ACTIVE" : "CUSTOMER SUPPORT MODE"}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop static sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-72 flex-col border-r border-[var(--border)] bg-[var(--surface-2)]/60 glass shrink-0"
      >
        {inner}
      </aside>

      {/* Mobile slide-in drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={close} data-testid="sidebar-drawer"
          >
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute left-0 top-0 h-full w-72 max-w-[85vw] flex flex-col ios-safe-top ios-safe-bottom bg-[var(--surface-2)] glass border-r border-[var(--border)]"
              onClick={(e) => e.stopPropagation()}
            >
              {inner}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
